import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, X, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SCRIPT = {
  start: {
    text: "Hi! I'm Duo AI Assistant ðŸ¤– Welcome to Duocapital. Are you looking to start investing today?",
    options: [
      { label: "Yes, I have funds to invest", next: "has_funds" },
      { label: "I want to invest but need a loan", next: "needs_loan" },
      { label: "I have general questions", next: "faq_menu" },
    ],
  },
  has_funds: {
    text: "Start by activating an AI Bot subscription tier. That tier sets the ROI rate for your future real investment.",
    options: [
      { label: "Starter bot - $1,000 / 15% ROI rate", next: "tier_pick", action: { type: "go", to: "/invest" } },
      { label: "Growth bot - $5,000 / 22% ROI rate", next: "tier_pick", action: { type: "go", to: "/invest" } },
      { label: "Premium bot - $8,000 / 30% ROI rate", next: "tier_pick", action: { type: "go", to: "/invest" } },
      { label: "Elite bot - $10,000 / 40% ROI rate", next: "tier_pick", action: { type: "go", to: "/invest" } },
      { label: "Platinum VIP bot - $25K+ / 55% ROI rate", next: "tier_pick", action: { type: "go", to: "/invest" } },
    ],
  },
  tier_pick: {
    text: "Perfect. I am taking you to the AI Bot Subscription page. Once your bot is active, Growth through Platinum become real investment options.",
    options: [{ label: "Got it", next: "end" }],
  },
  needs_loan: {
    text: "Duocapital loans unlock after AI Bot activation. Your maximum loan depends on the bot tier you choose, and approved funds can only be used as real investment credit. Would you like to apply?",
    options: [
      { label: "Yes, apply for a loan", next: "loan_flow", action: { type: "go_auth", to: "/dashboard?loan=1" } },
      { label: "Tell me more first", next: "loan_more" },
    ],
  },
  loan_more: {
    text: "Loan caps are tier-based: Starter $2,000, Growth $5,000, Premium $8,000, Elite $10,000, and Platinum VIP $15,000. Approved funds are not withdrawable cash; they fund real investments.",
    options: [
      { label: "I'd like to apply now", next: "loan_flow", action: { type: "go_auth", to: "/dashboard?loan=1" } },
      { label: "Back to main menu", next: "start" },
    ],
  },
  loan_flow: {
    text: "Great - I am taking you to your dashboard where you can submit the loan application.",
    options: [{ label: "Okay", next: "end" }],
  },
  faq_menu: {
    text: "Sure â€” pick a topic:",
    options: [
      { label: "How does it work?", next: "faq_how" },
      { label: "Is my money safe?", next: "faq_safe" },
      { label: "How do I withdraw?", next: "faq_withdraw" },
      { label: "What's the minimum investment?", next: "faq_min" },
      { label: "Back", next: "start" },
    ],
  },
  faq_how: {
    text: "Activate an AI Bot subscription tier first. Then fund a real investment starting at Growth ($5,000). Your bot tier ROI is applied to that real investment cycle.",
    options: [{ label: "Back", next: "faq_menu" }, { label: "Start investing", next: "has_funds" }],
  },
  faq_safe: {
    text: "We use bank-grade encryption, 256-bit SSL, and 24/7 monitoring. Funds are managed by a professional desk + AI co-pilot.",
    options: [{ label: "Back", next: "faq_menu" }],
  },
  faq_withdraw: {
    text: "From your dashboard â†’ Withdrawals â†’ enter your wallet â†’ submit. Processing takes 24â€“48 hours.",
    options: [{ label: "Back", next: "faq_menu" }],
  },
  faq_min: {
    text: "AI Bot subscriptions start at $1,000. Real investments start at $5,000 with the Growth tier.",
    options: [{ label: "Back", next: "faq_menu" }, { label: "Start investing", next: "has_funds" }],
  },
  end: {
    text: "Anything else I can help you with?",
    options: [
      { label: "Restart conversation", next: "start" },
      { label: "Close chat", next: "close" },
    ],
  },
};

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [stateKey, setStateKey] = useState("start");
  const endRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: "bot", text: SCRIPT.start.text }]);
      setStateKey("start");
    }
  }, [open, messages.length]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleChoice = (opt) => {
    setMessages((m) => [...m, { from: "user", text: opt.label }]);
    if (opt.action?.type === "go") {
      setTimeout(() => nav(opt.action.to), 600);
    }
    if (opt.action?.type === "go_auth") {
      setTimeout(() => {
        const tok = localStorage.getItem("duo_token");
        nav(tok ? opt.action.to : "/login");
      }, 600);
    }
    if (opt.next === "close") {
      setTimeout(() => setOpen(false), 400);
      return;
    }
    setTimeout(() => {
      const s = SCRIPT[opt.next];
      setStateKey(opt.next);
      setMessages((m) => [...m, { from: "bot", text: s.text }]);
    }, 500);
  };

  const current = SCRIPT[stateKey];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-50 w-16 h-16 rounded-full bg-[#D4AF37] text-[#0A0E27] shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="chatbot-fab"
        style={{ display: open ? "none" : "flex" }}
        aria-label="Open chat"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute top-1 right-1 w-3 h-3 bg-[#00D68F] rounded-full blink"></span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-96 h-[560px] glass-strong rounded-2xl flex flex-col overflow-hidden shadow-2xl"
            data-testid="chatbot-window"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0A0E27]">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[#0A0E27]" />
                  <span className="absolute -bottom-0 -right-0 w-3 h-3 bg-[#00D68F] rounded-full border-2 border-[#0A0E27] blink"></span>
                </div>
                <div>
                  <div className="font-semibold text-sm">Duo AI Assistant</div>
                  <div className="text-[10px] text-[#00D68F] uppercase tracking-widest">Online</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="chatbot-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-hide" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.from === "user"
                        ? "bg-[#D4AF37] text-[#0A0E27] rounded-tr-sm"
                        : "bg-white/10 text-white rounded-tl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-white/10 bg-[#0A0E27] space-y-2">
              {current.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleChoice(opt)}
                  className="w-full text-left text-sm px-3 py-2 rounded-md bg-white/5 hover:bg-[#D4AF37]/15 hover:text-[#D4AF37] border border-white/5 transition-colors"
                  data-testid={`chatbot-option-${i}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
